import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import OptimizedImage from "@/components/OptimizedImage";
import heroImage from "@/assets/hero-image.jpg";
import { useContentSnippets } from "@/hooks/useContentSnippets";

interface HeroProps {
  imageSrc?: string;
  imageAlt?: string;
  title?: string;
  showButtons?: boolean;
}

const Hero = ({ 
  imageSrc = heroImage, 
  imageAlt = "Drei diverse medizinische Fachkräfte im Gespräch",
  title,
  showButtons = true
}: HeroProps) => {
  const { getSnippet, loading } = useContentSnippets();

  return (
    <section className="relative h-[700px] flex items-center justify-center text-white">
      <div className="absolute inset-0 bg-black/60"></div>
      <OptimizedImage
        className="absolute inset-0 w-full h-full object-cover" 
        src={imageSrc}
        alt={imageAlt}
        priority={true}
      />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-center gap-8">
          <div className="text-center max-w-4xl">
            {title ? (
              <h1 className="mb-8 [text-shadow:_3px_3px_12px_rgb(0_0_0_/_90%)]">
                {title}
              </h1>
            ) : (
              <>
                <h1 className="mb-12 [text-shadow:_3px_3px_12px_rgb(0_0_0_/_90%)]">
                  {loading
                    ? "KI-Weiterbildung für Führungskräfte in der Pflege – Menschenzentriert"
                    : getSnippet(
                        "home_hero_h1",
                        "KI-Weiterbildung für Führungskräfte in der Pflege – Menschenzentriert"
                      )}
                </h1>
                <h2 className="text-xl md:text-2xl font-semibold mb-8 [text-shadow:_2px_2px_10px_rgb(0_0_0_/_90%)]">
                  {loading
                    ? "MAIP: Das Management-Programm, das Sie befähigt, KI erfolgreich und zum Wohl des Menschen einzuführen."
                    : getSnippet(
                        "home_hero_subtitle",
                        "MAIP: Das Management-Programm, das Sie befähigt, KI erfolgreich und zum Wohl des Menschen einzuführen."
                      )}
                </h2>
              </>
            )}
            {showButtons && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/modul-0">
                  <Button 
                    size="lg" 
                    className="bg-primary hover:bg-primary-hover text-primary-foreground px-8 py-6 text-lg font-semibold transition-all shadow-lg hover:shadow-xl"
                  >
                    Jetzt kostenfrei starten
                  </Button>
                </Link>
                <Link to="/warum-maip">
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-2 border-white text-white bg-black/30 backdrop-blur-sm hover:bg-white hover:text-foreground px-8 py-6 text-lg font-semibold transition-all shadow-lg hover:shadow-xl"
                  >
                    Mehr erfahren
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {!title && (
        <div className="hidden lg:block absolute right-12 top-24 z-20">
          <div className="w-56 h-56 bg-red-600 rounded-full flex items-center justify-center shadow-2xl animate-pulse">
            <div className="text-white font-bold text-center text-lg px-6 leading-tight">
              <div className="mb-2">Kurse ab 2026</div>
              <div className="text-base mb-2">Wir freuen uns auf Ihre Kontaktaufnahme</div>
              <div className="text-base">+49 221 16853308</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
